<template>
    <div class="pos-rel py-5">
        <div
            v-if="fields.slantedBackground"
            class="primary background first"
            :style="backgroundStyle"
        />
        <div
            :class="[
                { second: fields.slantedBackground },
                backgroundColorClass(fields.backgroundColor)
            ]"
            class="background"
            :style="backgroundStyle"
        />

        <div class="content-center my-10">
            <div class="content-constraint pos-rel" style="z-index: 1;">
                <v-row v-if="fields" no-gutters :style="`width:${fields.width}%`">
                    <v-col
                        :cols="cols"
                        :order="align === 'right' ? 1 : 0"
                        :class="textAlignment"
                        class="content-section-wrapper"
                    >
                        <h2
                            v-if="fields.title && fields.title.length"
                            :class="[textColorClass(fields.backgroundColor)]"
                        >
                            {{ fields.title }}
                        </h2>
                        <ctf-rich-text
                            v-if="
                                fields.description &&
                                fields.description.content.length
                            "
                            class="mt-5 bcbs-content"
                            :class="[textColorClass(fields.backgroundColor)]"
                            :document="fields.description"
                        />
                        <div class="d-flex flex-wrap justify-center">
                            <ctf-link
                                class="mx-2 mt-5"
                                v-if="fields.button"
                                :btn-id="fields.button.sys.id"
                                :inverted="fields.backgroundColor === 'orange'"
                            />
                            <ctf-link
                                class="mx-2 mt-5"
                                v-if="fields.secondaryButton"
                                :btn-id="fields.secondaryButton.sys.id"
                                :inverted="fields.backgroundColor === 'orange'"
                            />
                        </div>
                    </v-col>
                    <v-col
                        v-if="smallImage"
                        :order="align === 'right' ? 0 : 1"
                        :cols="cols"
                        class="d-flex justify-center"
                        :class="imageClasses"
                    >
                        <v-img
                            :src="smallImage.url"
                            :alt="smallImage.description || smallImage.title"
                            max-width="417"
                            min-width="333"
                            width="100%"
                            contain
                        />
                    </v-col>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import CtfLink from '@/components/snippets/ctf-link'
import { getCTFAsset } from '@/plugins/contentful'
import { LAYOUT_TYPES } from '@/store/modules/layout'
import { backgroundColorClass, textColorClass } from '@/utils/helpers'

export default {
  name: 'StandaloneText',
  components: {
    CtfLink
  },
  props: {
    fields: {
      type: Object,
      default: null
    },
    assets: {
      type: Array,
      default: null
    },
    sliceRefs: {
      type: Object,
      default: null
    },
    enrollmentBannerIsBelow: {
      type: Boolean,
      default: false
    },
    enrollmentBannerIsAbove: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // reference to the div below this component so background can extend into it if needed
      textAlignment: this.fields.textAlignment
        ? 'text-' + this.fields.textAlignment
        : 'text-center',
      elementBelow: null,
      elementAbove: null,
      smallImage: null
    }
  },
  computed: {
    backgroundStyle () {
      // extend background half way into element below this component
      const bottom =
                this.enrollmentBannerIsBelow && this.elementBelow
                  ? `-${Math.floor(this.elementBelow.clientHeight / 2)}px`
                  : 0
      const top =
                this.enrollmentBannerIsAbove && this.elementAbove
                  ? `-${Math.floor(this.elementAbove.clientHeight / 2)}px`
                  : 0
      const style = { bottom }
      if (!this.fields.slantedBackground) {
        style.top = top
      }
      return style
    },
    align () {
      return this.fields.textAlignment
        ? this.fields.textAlignment
        : 'center'
    },
    cols () {
      if (!this.fields.smallImage || this.$vuetify.breakpoint.smAndDown) {
        return 12
      }
      return this.fields.textAlignment === 'right' ||
                this.fields.textAlignment === 'left'
        ? 6
        : 12
    },
    imageClasses () {
      return {
        'mt-10': (this.$vuetify.breakpoint.smAndDown && (this.align === 'left' || this.align === 'center')) ||
        (this.align !== 'left' && this.align !== 'right'),
        'mb-10': this.$vuetify.breakpoint.smAndDown && this.align === 'right'
      }
    }
  },
  watch: {
    fields: {
      handler () {
        if (this.fields.slantedBackground) {
          this.setHasSlantedBackground(true)
        }
      },
      immediate: true,
      deep: true
    }
  },
  created () {
    if (this.fields.smallImage) {
      const img = getCTFAsset(this.fields.smallImage.sys.id, this.assets)
      this.smallImage = {
        title: img.fields.title,
        description: img.fields.description,
        url: img.fields.file.url
      }
    }
  },
  mounted () {
    if (this.sliceRefs) {
      const keyB = Object.keys(this.sliceRefs.refs).find(
        (el, i) => i === this.sliceRefs.index + 1
      )
      this.elementBelow = keyB ? this.sliceRefs.refs[keyB][0] : null
      const keyA = Object.keys(this.sliceRefs.refs).find(
        (el, i) => i === this.sliceRefs.index - 1
      )
      this.elementAbove = keyA ? this.sliceRefs.refs[keyA][0] : null
    }
  },
  methods: {
    ...mapMutations({
      setHasSlantedBackground:
                LAYOUT_TYPES.mutations.SET_PAGE_HAS_SLANTED_BACKGROUND
    }),
    backgroundColorClass,
    textColorClass
  }
}
</script>

<style lang="scss" scoped>
@import '../../../styles/variables';

.v-expansion-panel-content {
    padding-top: 24px;
}

.content-section-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;

    .read-more {
        text-align: center;
        margin-top: 2em;
        a {
            font-weight: $font-weight-bold;
        }
    }
}

.text-left {
    align-items: flex-start;
}

.text-center {
    align-items: center;
}

.text-right {
    align-items: flex-end;
}

.content-section-wrapper--light-blue {
    @extend .content-section-wrapper;
    background-color: $primary;
}

.background {
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
}

.first {
    top: -10vw;
    clip-path: polygon(0 0, 100% 10vw, 100% 100%, 0% 100%);
}
.second {
    top: -10vw;
    clip-path: polygon(0 10vw, 100% 0, 100% 100%, 0% 100%);
}
</style>
